/* CardPriorityBadge — pill shown on every planner chip + inside the
 * card drawer to let an operator triage the designer queue.
 *
 * Priority semantics (operator-defined, Feb 2026):
 *   1 = highest urgency  → red, designers pick up first
 *   2 = high             → orange
 *   3 = medium           → blue
 *   4 = low              → slate
 *   null = untriaged     → muted dashed border, "—"
 *
 * The badge itself is the click target. Tapping opens a tiny popover
 * with P1 / P2 / P3 / P4 / Clear buttons. The popover saves via PATCH
 * /v2/cards/{id} (the standard CardPatch route) so versioning + audit
 * trail come along for free.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const _API_BASE = (typeof window !== "undefined" && window.location.hostname !== "localhost")
  ? window.location.origin
  : (process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : ""));
const API = `${_API_BASE}/api`;

export const PRIORITY_META = {
  1: { label: "P1", title: "Priority 1 — highest urgency",  cls: "bg-rose-200    text-rose-900    border-rose-400" },
  2: { label: "P2", title: "Priority 2 — high",             cls: "bg-orange-200  text-orange-900  border-orange-400" },
  3: { label: "P3", title: "Priority 3 — medium",           cls: "bg-sky-200     text-sky-900     border-sky-400" },
  4: { label: "P4", title: "Priority 4 — low",              cls: "bg-slate-200   text-slate-700   border-slate-400" },
};
const UNSET_CLS = "bg-white/70 text-slate-400 border-dashed border-slate-400";

export function CardPriorityBadge({
  card,
  workspaceId,
  size = "sm",            // "sm" (chip) | "md" (drawer)
  onChange,               // optional — called with the updated card after save
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const priority = card?.priority ?? null;
  const meta = priority ? PRIORITY_META[priority] : null;
  const label = meta?.label || "—";
  const cls = meta?.cls || UNSET_CLS;
  const title = meta?.title || "Set priority";

  const setPriority = async (next) => {
    if (saving || disabled) return;
    if ((card?.priority ?? null) === next) { setOpen(false); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/cards/${card.card_id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ priority: next, expected_version: card.version }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e?.detail || `Save failed (${r.status})`);
      }
      const body = await r.json();
      const updated = body?.card || body;
      onChange?.(updated);
      toast.success(next == null ? "Priority cleared" : `Priority set to P${next}`);
      setOpen(false);
    } catch (e) {
      toast.error(typeof e?.message === "string" ? e.message : "Could not save priority");
    } finally {
      setSaving(false);
    }
  };

  const sizing = size === "md"
    ? "text-xs px-2 py-0.5"
    : "text-[9px] px-1.5 py-[1px]";

  return (
    <span ref={ref} className="relative inline-flex" data-testid={`priority-badge-wrap-${card.card_id}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={(e) => { e.stopPropagation(); if (!disabled) setOpen((v) => !v); }}
        title={title}
        aria-label={`Priority: ${label}`}
        data-testid={`priority-badge-${card.card_id}`}
        data-priority={priority ?? "unset"}
        className={`uppercase tracking-wider font-bold rounded border inline-flex items-center gap-0.5 transition ${cls} ${sizing} ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:brightness-95"}`}
      >
        {label}
      </button>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute z-40 top-full mt-1 left-0 bg-white border border-slate-300 rounded-lg shadow-xl p-1.5 flex items-center gap-1 text-slate-900"
          data-testid={`priority-popover-${card.card_id}`}
        >
          {[1, 2, 3, 4].map((p) => {
            const m = PRIORITY_META[p];
            const active = (card?.priority ?? null) === p;
            return (
              <button
                key={p}
                type="button"
                disabled={saving}
                onClick={(e) => { e.stopPropagation(); setPriority(p); }}
                title={m.title}
                data-testid={`priority-set-${card.card_id}-p${p}`}
                className={`text-[10px] uppercase tracking-wider font-bold rounded border px-1.5 py-1 transition ${m.cls} ${active ? "ring-2 ring-indigo-500" : "hover:brightness-95"}`}
              >
                {m.label}
              </button>
            );
          })}
          <span className="w-px h-5 bg-slate-200 mx-0.5" />
          <button
            type="button"
            disabled={saving || (card?.priority ?? null) === null}
            onClick={(e) => { e.stopPropagation(); setPriority(null); }}
            title="Clear priority"
            data-testid={`priority-clear-${card.card_id}`}
            className="text-[10px] uppercase tracking-wider font-semibold rounded border px-1.5 py-1 bg-white text-slate-600 border-slate-300 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Clear
          </button>
        </div>
      )}
    </span>
  );
}

export default CardPriorityBadge;
